import React, { useEffect, useRef, useCallback } from 'react';
import Icon from './Icon';

/**
 * Modal acessível: fecha no Escape, devolve o foco a quem abriu, prende o foco
 * dentro do diálogo e trava a rolagem do fundo.
 *
 * Os modais do app eram `div.modal-overlay` soltos, sem role, sem Escape e sem
 * controle de foco — quem usa teclado ou leitor de tela ficava preso na página
 * de trás. No celular, a rolagem do fundo vazando pelo modal também incomodava.
 *
 * CORREÇÃO iOS: o useEffect agora roda apenas na montagem ([]). As referências
 * de onClose e bloqueado são acessadas via ref para que o listener de Escape
 * sempre use o valor mais recente sem re-executar o efeito (que fazia .focus()
 * e roubava o foco do input no iOS a cada keystroke).
 */
export default function Modal({ titulo, onClose, children, rodape, largura, bloqueado }) {
  const caixaRef = useRef(null);
  const anterior = useRef(null);
  const onCloseRef = useRef(onClose);
  const bloqueadoRef = useRef(bloqueado);

  // Mantém refs atualizadas sem disparar o efeito.
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { bloqueadoRef.current = bloqueado; }, [bloqueado]);

  useEffect(() => {
    anterior.current = document.activeElement;
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Foca o primeiro controle do diálogo, ou a própria caixa.
    const foco = caixaRef.current?.querySelector(
      'input:not([type=hidden]), select, textarea, button, [href]'
    );
    (foco || caixaRef.current)?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !bloqueadoRef.current && onCloseRef.current) {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      // Trap: Tab no último volta ao primeiro e Shift+Tab no primeiro vai ao último.
      const focaveis = caixaRef.current?.querySelectorAll(
        'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focaveis || focaveis.length === 0) return;
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflowAntes;
      anterior.current?.focus?.();
    };
  }, []); // Roda apenas na montagem — sem re-focus a cada render.

  const handleOverlayClick = useCallback(() => {
    if (!bloqueadoRef.current && onCloseRef.current) onCloseRef.current();
  }, []);

  return (
    <div
      className="modal-overlay"
      onClick={bloqueado ? undefined : handleOverlayClick}
      role="presentation"
    >
      <div
        className="modal"
        ref={caixaRef}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        style={largura ? { maxWidth: largura } : undefined}
        onClick={e => e.stopPropagation()}
      >
        {(titulo || onClose) && (
          <div className="modal-header">
            <h3>{titulo}</h3>
            {onClose && !bloqueado && (
              <button type="button" className="modal-close" onClick={onClose} aria-label="Fechar">
                <Icon nome="fechar" tamanho={18} />
              </button>
            )}
          </div>
        )}
        {children}
        {rodape && <div className="actions modal-rodape">{rodape}</div>}
      </div>
    </div>
  );
}
